import { useWorkflowStateMachine } from 'hooks/useWorkflowStateMachine';
import { WorkflowStateStep } from 'hooks/useWorkflowStateMachine/types';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getIsFoodCourt, getRestaurantId, getNeedManualTableId } from 'state/app';
import { getIsOrderFollowUpEnabled } from 'state/brand';
import { getStatuses } from 'state/ordersStatus/ordersStatus.slice';
import { getAccessToken } from 'state/customer/customer.slice';
import { useAppSelector } from 'state/store';
import { computeSvyStepperSteps } from './useCartStepper.utils';
import { StepperWorkflowStates } from './useCartStepper.types';

export const useCartStepper = () => {
    const isFoodCourt = useAppSelector(getIsFoodCourt);
    const restaurantId = useAppSelector(getRestaurantId);
    const isOrderFollowUpEnabled = useAppSelector(getIsOrderFollowUpEnabled(restaurantId));
    const isAuthenticated = useAppSelector(getAccessToken);
    const ordersStatus = useAppSelector(getStatuses);
    const needManualTableId = useAppSelector(getNeedManualTableId);

    const { state } = useWorkflowStateMachine();
    const { t } = useTranslation();

    const stepperWorkflowStates: StepperWorkflowStates = computeSvyStepperSteps(
        isFoodCourt,
        isOrderFollowUpEnabled,
        !!isAuthenticated,
        needManualTableId,
    );

    const indexOfState = stepperWorkflowStates
        .map((stepperWorkflowState) => stepperWorkflowState.stateValue)
        .indexOf(state.value as WorkflowStateStep);

    const getIsCompleted = useCallback(
        (workflowIndex: number) => {
            if (!isFoodCourt) {
                return workflowIndex === indexOfState;
            }

            if (workflowIndex !== indexOfState) {
                return false;
            }

            return (
                !ordersStatus ||
                Object.values(ordersStatus).every(
                    ({ status }) => status === 'COLLECTED' || status === 'CANCELLED',
                )
            );
        },
        [isFoodCourt, ordersStatus, indexOfState],
    );

    useEffect(() => {
        // top: -100 is a workaround to bypass the
        // bottom bar height being computed as page height...
        window.scrollTo({ top: -100, behavior: 'auto' });
    }, [state.value]);

    const steps =
        indexOfState !== -1
            ? [
                  ...Object.entries(stepperWorkflowStates).map(
                      ([_index, workflowState], index) => ({
                          label: t(workflowState.translation),
                          active: indexOfState >= workflowState.index,
                          completed:
                              index + 1 === stepperWorkflowStates.length
                                  ? getIsCompleted(workflowState.index)
                                  : indexOfState > workflowState.index,
                      }),
                  ),
              ]
            : undefined;

    return {
        steps,
    };
};
